import layoutStyles from '@ds/foundations/layout.module.css';
import { type LayoutElementProps, type LayoutProps, resolveLayoutProps } from '@ds/foundations/layoutProps';
import paddingStyles from '@ds/foundations/padding.module.css';
import { type PaddingProps, resolvePaddingProps } from '@ds/foundations/paddingProps';
import { Slot } from '@ds/foundations/Slot';
import clsx from 'clsx';
import React, { type CSSProperties, forwardRef, type ReactNode } from 'react';

import styles from './Box.module.css';

/**
 * A general-purpose layout primitive for applying token-based padding and one-off geometry.
 *
 * `Box` has no visual appearance of its own — it only contributes spacing and, through the
 * shared **layout props** (`position`, offsets, size, `overflow`, flex-item props — see
 * `layoutProps.ts`), the positioning a call site would otherwise write in `style`.
 * Use it to add padding to a region without introducing flex or grid context.
 *
 * All padding values map to `--space-*` design tokens and scale responsively. The padding props
 * are shared with `Flex` (and, block-axis only, `Grid` and `Container`) — see `paddingProps.ts`.
 *
 * **Cascade order** (highest priority last):
 * `p` → `px` / `py` → `pt` / `pr` / `pb` / `pl`
 *
 * @example
 * // Uniform padding
 * <Box p={6}><Card /></Box>
 *
 * @example
 * // Asymmetric padding — more space on the sides than top/bottom
 * <Box py={4} px={8}><Content /></Box>
 *
 * @example
 * // Semantic element — renders a <section> with padding
 * <Box as="section" p={8}>...</Box>
 *
 * @example
 * // Layout props — a sticky, width-capped region, no style prop
 * <Box position="sticky" top={0} maxWidth="40rem" overflow="hidden">...</Box>
 */
export interface BoxProps extends React.HTMLAttributes<HTMLElement>, LayoutElementProps, PaddingProps, LayoutProps {
    children?: ReactNode;
}

const Box = forwardRef<HTMLElement, BoxProps>(
    ({ as: Tag = 'div', asChild, className, style, children, ...others }, ref) => {
        // Padding props split off first, then the layout props (position / offsets / size / overflow /
        // flex-item). Both become gated data-* attributes + custom properties, read by the shared
        // padding.module.css and layout.module.css. Any caller-supplied `style` is spread last and wins.
        const padding = resolvePaddingProps(others);
        const { attrs, vars, rest } = resolveLayoutProps(padding.rest);

        // asChild renders the one child element instead, with these props merged onto it (Slot).
        const Comp: React.ElementType = asChild ? Slot : Tag;

        return (
            <Comp
                ref={ref}
                {...padding.attrs}
                {...attrs}
                className={clsx(styles.box, paddingStyles.padding, layoutStyles.layout, className)}
                style={{ ...padding.vars, ...vars, ...style } as CSSProperties}
                {...rest}
            >
                {children}
            </Comp>
        );
    }
);

Box.displayName = 'Box';

export { Box };
export default Box;
